/* eslint-disable max-lines */
import { Injectable } from '@angular/core';
import { ParamMap, Router } from '@angular/router';
import { iif, Observable, of, zip } from 'rxjs';
import { filter, map, switchMap, take, tap } from 'rxjs/operators';
import isEmpty from 'lodash-es/isEmpty';

import { isNullOrUndefined } from '@app/util/util';
import { DETAIL_PACKAGE_STEP } from '@packages/packages-search-bar/constants/package-flux-params';
import { PackageStep } from '@packages/packages-search-bar/constants/package-step';
import { BaseSearchBarFacade } from '@shared/base-search-bar/base-search-bar-facade';
import { HotelDetailPackagePayload, HotelByCodePackagePayload } from '@packages/packages-result/entities/hotel-detail';
import { HotelDetailDeepLinkPayload, HotelDetailPayload } from '@hotels/hotel-detail/entities/hotel-detail.payload';
import {
  mapHotelDetailPackagesPayload, mapHotelDetailDeeplinkPackagesPayload, mapHotelDetailDeepLinkPayload, mapHotelDetailPayload,
} from '@hotels/hotel-detail/mappers/hotel-detail.mapper';
import { TravelerTypes } from '@pax/entities/traveler-info';
import { MobileDeviceService } from '@app/services/mobile-device.service';
import {
  getParamByPackResultURL, hotelResultsToPackageFlightRouteMapper, setBasePackageResultURL, PACKAGES_RESULT_URL_PARAMS_POSITION
} from '@packages/packages-result/mappers/package-routing.mapper';
import { HotelDetailFacade } from '@hotels/hotel-detail/hotel-detail.facade';
import { BRANCH_CODE } from '@app/modules/core/constants/query-params';
import { HotelDetailResponse } from '@hotels/hotel-detail/entities/hotel-detail.response';
import { formatDateByRegex } from '@ux/mappers/date.mapper';
import { FlightType } from '@search-bar/entities/flight-type';
import { SelectAutocompletePlace } from '@search-bar/entities/select-autocomplete-place';
import { PlaceType } from '@shared/base-search-bar/entities/place-type';
import { PlacePipe } from '@app/pipes/place.pipe';
import { SearchData } from '@shared/base-search-bar/entities/search-data';
import { SearchBy } from '@shared/base-search-bar/entities/search.entities';
import { bookingRootRoute } from '@booking/booking-routing.module';
import { AMPLITUDE_EVENTS } from '@app/modules/core/constants/amplitude.constants';
import {
  ARRIVAL_DATE_HOTEL_DETAIL, ARRIVAL_HOTEL_DETAIL, DEPARTURE_DATE_HOTEL_DETAIL, SESSION_TOKEN_HOTEL_DETAIL
} from '@hotels/hotel-detail/constants/hotel-detail-route-params';
import { LocalStorageService } from '@app/services/local-storage.service';
import { GuestToPassengerUIPipe } from '@app/pipes/guest-to-paseenger.pipe';
import { Room } from '@shared/base-search-bar/entities/room.entities';
import { hotelAmplitudeProductPropertiesMapper } from '@app/modules/core/mappers/hotels-amplitude.mapper';
import { TravelBookingType } from '@pax/entities/travel-booking-types';
import { PropertiesAmplitudeService } from '@app/services/properties-amplitude.service';
import { FlightClass } from '@shared/base-search-bar/entities/flight-class';
import { HotelDetailFluxType } from '@hotels/hotel-detail/entities/hotel-detail-type';
import { homeRootRoute } from '@home/home-routing.module';
import {
  getDeeplinkUrlBySessionTokenUrl, setPackageResultUrlBySessionTokenUrl, getHotelDetailFluxType,
  isPackageHotelDetail, isValidPackageDetailStep, isValidPackageStorageItem, searchByHotelDetail
} from '@hotels/hotel-detail/mappers/hotel-detail-action.mapper';
import { TriggerAmplitudeService } from '@amplitude/services/trigger-amplitude.service';
import { triggerAmplitudeEventApp } from '@amplitude/helpers/gtm-functions';
import { HotelResultsFacade } from '@hotels/hotel-results/hotel-results.facade';
import { RoomEvent } from '@hotels/hotel-detail/components/hotel-room-price/hotel-room-price.component';
import { getDestinationByV2 } from '@packages/packages-search-bar/mappers/packages-url.mapper';
import { DeeplinkPlacePipe } from '@app/pipes/deeplink-place.pipe';
import { HotelDestinationInputType, LocationType } from '@shared/base-search-bar/entities/autocomplete-destionation';
import { PlaceNamePipe } from '@app/pipes/place-name.pipe';
import { PlaceSelectors } from '@app/entities/place';

@Injectable()
export class HotelDetailActionService {

  private hotelParams: HotelDetailDeepLinkPayload | HotelDetailPayload |
    HotelDetailPackagePayload | HotelByCodePackagePayload = {} as HotelDetailPayload;

  constructor(
    private router: Router,
    private placePipe: PlacePipe,
    private facade: HotelDetailFacade,
    private resultsFacade: HotelResultsFacade,
    private searchFacade: BaseSearchBarFacade,
    private mobileDeviceService: MobileDeviceService,
    private localStorageService: LocalStorageService,
    private guestToPassengerUIPipe: GuestToPassengerUIPipe,
    private amplitude: PropertiesAmplitudeService,
    private triggerAmplitudeService: TriggerAmplitudeService,
    private readonly departurePipe: DeeplinkPlacePipe,
    private readonly placeNamePipe: PlaceNamePipe,
  ) { }

  private get isMobile$(): Observable<boolean> {
    return this.mobileDeviceService.isMobileDevice$;
  }

  private get hotelDetail$(): Observable<HotelDetailResponse> | null {
    return this.facade.hotelDetail$.pipe(map(detail => detail.Info || detail.Result ? detail : null));
  }

  public listerUrlParamsByHotelDetail(
    paramMap: Observable<ParamMap>, queryParamMap: Observable<ParamMap>): Observable<SearchData> {
    return paramMap.pipe(
      switchMap(params => this.isMobile$.pipe(
        take(1),
        map(isMobile => ({ params, isMobile }))
      )),
      map(({ params, isMobile }) => ({
        params, isMobile, hasPackageStep: !!params.get(DETAIL_PACKAGE_STEP),
        hasSessionToken: !!params.get(SESSION_TOKEN_HOTEL_DETAIL),
        fluxType: getHotelDetailFluxType(params)
      })),
      tap(({ params, hasSessionToken, hasPackageStep, fluxType, isMobile }) => {
        if (hasPackageStep && !isValidPackageDetailStep(params)) this.router.navigate([homeRootRoute]);
        else {
          switch (fluxType) {
            case HotelDetailFluxType.HOTEL_DETAIL:
              if (!hasSessionToken) this.hotelParams = mapHotelDetailDeepLinkPayload(params, isMobile);
              else this.hotelParams = mapHotelDetailPayload(params);
              this.resetGuestData(fluxType);
              break;
            case HotelDetailFluxType.AIR_PACKAGE_HOTEL_DETAIL:
              this.searchFacade.setPackageStep(params.get(DETAIL_PACKAGE_STEP));
              if (hasSessionToken) this.hotelParams = mapHotelDetailPackagesPayload(params);
              else this.hotelParams = mapHotelDetailDeeplinkPackagesPayload(params, isMobile);
              this.resetGuestData(fluxType);
              break;
          }
        }
      }),
      filter(({ hasPackageStep, params }) => hasPackageStep ? isValidPackageDetailStep(params) : true),
      switchMap(({ params, fluxType, hasSessionToken, isMobile }) => zip(
        of({ params, fluxType, hasSessionToken, isMobile }), queryParamMap.pipe(take(1)), this.searchFacade.searchData$,
      )),
      map(([{ params, fluxType, hasSessionToken, isMobile }, queries, searchData]) => {
        const storage = {
          branchCode: queries.get(BRANCH_CODE), isValidPackageStorage: null,
          packageSearchData: null, packageAirSelected: null, packageSetResultUrl: null
        };
        switch (fluxType) {
          case HotelDetailFluxType.HOTEL_DETAIL:
            return { params, fluxType, hasSessionToken, searchData, queries, isMobile, storage };
          case HotelDetailFluxType.AIR_PACKAGE_HOTEL_DETAIL:
            const packageResultsUrl = this.localStorageService.getDecodePackageBaseUrlResult();
            const packageSearchData = this.localStorageService.getDecodePackageSearch();
            const packageAirSelected = this.localStorageService.getDecodePackageAirSelected();
            const packageSetResultUrl = hasSessionToken ? packageResultsUrl :
              setBasePackageResultURL(packageResultsUrl, this.router.url.split('?')[0]);
            const isValidPackageStorage = isValidPackageStorageItem(packageSearchData) &&
              isValidPackageStorageItem(packageAirSelected) && !!packageSetResultUrl;
            return {
              params, fluxType, hasSessionToken, searchData, queries, isMobile,
              storage: { ...storage, packageSearchData, packageAirSelected, packageSetResultUrl, isValidPackageStorage }
            };
        }
      }),
      tap(({ fluxType, storage }) => {
        if (isPackageHotelDetail(fluxType) && !storage.isValidPackageStorage) this.router.navigate([homeRootRoute]);
      }),
      filter(({ fluxType, storage }) => isPackageHotelDetail(fluxType) ? !!storage.isValidPackageStorage : true),
      tap(({ fluxType, isMobile, storage, hasSessionToken }) => {
        if (storage.branchCode) this.searchFacade.setBranchCode(storage.branchCode);
        switch (fluxType) {
          case HotelDetailFluxType.HOTEL_DETAIL:
            if (!hasSessionToken) {
              this.facade.setHotelResultUrlInHotelDetail(this.router.url.split('?')[0]);
            }
            break;
          case HotelDetailFluxType.AIR_PACKAGE_HOTEL_DETAIL:
            this.localStorageService.setEncodePackageBaseUrlResult(storage.packageSetResultUrl);
            if (!hasSessionToken) this.facade.setPackageBaseUrlResultInHotelDetail(storage.packageSetResultUrl);
            this.facade.savePackageSearchData(JSON.parse(storage.packageSearchData));
            if (isMobile) this.facade.setAirSelected(JSON.parse(storage.packageAirSelected));
            else this.facade.setAirSelectedDesktop(JSON.parse(storage.packageAirSelected));
            break;
        }
      }),
      switchMap(({ params, fluxType, hasSessionToken, storage }) => zip(
        of({ params, fluxType, hasSessionToken, storage }), this.searchFacade.searchData$
      )),
      map(([{ params, fluxType, hasSessionToken, storage }, searchData]) => ({
        params, fluxType, hasSessionToken, detailPayload: this.hotelParams, searchData, storage
      })),
      tap(({ fluxType, hasSessionToken, detailPayload, searchData }) => {
        switch (fluxType) {
          case HotelDetailFluxType.HOTEL_DETAIL:
            if (hasSessionToken) this.facade.fetchHotelDetail(detailPayload as HotelDetailPayload);
            else {
              const payload = detailPayload as HotelDetailDeepLinkPayload;
              const isByhours = !detailPayload.CheckOut;
              this.facade.fetchHotelDetailDeeplink({
                ...payload, RequestSettings: { ...payload.RequestSettings, BranchCode: searchData.branchCode }
              }, isByhours);
            }
            break;
          case HotelDetailFluxType.AIR_PACKAGE_HOTEL_DETAIL:
            if (hasSessionToken) this.facade.fetchHotelDetailPackage(detailPayload as HotelDetailPackagePayload);
            else {
              const payload = detailPayload as HotelByCodePackagePayload;
              this.facade.fetchHotelDetailByCodePackage({
                ...payload, RequestSettings: { ...payload.RequestSettings, BranchCode: searchData.branchCode }
              });
            }
            break;
        }
      }),
      switchMap(({ params, fluxType, hasSessionToken, storage }) => zip(
        of({ params, fluxType, hasSessionToken, storage }), this.hotelDetail$.pipe(filter(detail => !!detail && !isEmpty(detail)))
      )),
      tap(([{ params, fluxType, hasSessionToken, storage }, detail]) => {
        switch (fluxType) {
          case HotelDetailFluxType.HOTEL_DETAIL:
            if (hasSessionToken) {
              this.searchFacade.selectCalendarDates([new Date(detail.Result.CheckinDate), new Date(detail.Result.CheckoutDate)]);
              this.facade.setHotelResultUrlInHotelDetail(getDeeplinkUrlBySessionTokenUrl(detail, params));
            } else {
              this.setDestination(params.get(ARRIVAL_HOTEL_DETAIL), PlaceType.ARRIVAL);
              this.setCalendarByUrl(params);
            }
            break;
          case HotelDetailFluxType.AIR_PACKAGE_HOTEL_DETAIL:
            const result = storage.packageSetResultUrl;
            this.searchFacade.selectFlightType(FlightType.ROUNDTRIP);
            this.searchFacade.selectFlightClass(FlightClass.CHEAPER);
            this.getInputPlaceInfo(getParamByPackResultURL(
              result, PACKAGES_RESULT_URL_PARAMS_POSITION.DEPARTURE), null, null, null
            );
            this.getInputPlaceInfo(getParamByPackResultURL(
              result, PACKAGES_RESULT_URL_PARAMS_POSITION.DESTINATION
            ), detail?.Result?.Destination, detail?.Result?.FullAddress, detail?.Result?.HotelName);
            if (hasSessionToken) {
              this.searchFacade.selectCalendarDates([new Date(detail.Result.CheckinDate), new Date(detail.Result.CheckoutDate)]);
              this.facade.setPackageBaseUrlResultInHotelDetail(setPackageResultUrlBySessionTokenUrl(detail, params, result));
            } else this.setCalendarByUrl(params);
            break;
        }
      }),
      switchMap(({ fluxType }) => zip(of(fluxType), this.searchFacade.searchData$)),
      tap(([fluxType, searchData]) => this.searchFacade.saveSearchData(
        searchData, searchByHotelDetail(isPackageHotelDetail(fluxType))
      ))
    ).pipe(switchMap(() => this.searchFacade.searchData$));
  }

  public selectHotelRoom(hotelId: string, queryParamMap: Observable<ParamMap>, roomCode: string, roomData: RoomEvent): void {
    zip(
      this.facade.hotelDetail$.pipe(map(x => 
        hotelAmplitudeProductPropertiesMapper(x.Result, x.Info, roomData?.capacity, roomCode))),
      this.facade.hotelSearchDetail$.pipe(take(1)), this.searchFacade.packageStep$.pipe(take(1)),
      this.facade.hotelDetail$.pipe(map(detail => detail?.Result?.OptionId))
    ).pipe(
      map(([productProps, hotelData, packageStep, optionId]) => {
        const isPackageStepDetail = !!packageStep && packageStep === PackageStep.AIR_PACK_HOTEL_DETAIL;
        return { productProps, hotelData, isPackageStepDetail, optionId };
      }),
      switchMap(({ productProps, hotelData, isPackageStepDetail, optionId }) => zip(
        of({ hotelData, isPackageStepDetail }),
        iif(() => !isPackageStepDetail,
          zip(
            this.amplitude.searchProperties$(SearchBy.HOTELS),
            this.resultsFacade.hotelResults$.pipe(map(results => {
              if (results && results.length && !isNullOrUndefined(optionId)) {
                const index = results.findIndex(res => res.OptionId === optionId);
                return index >= 0 ? index + 1 : null;
              } else return null;
            }))
          ).pipe(
            switchMap(([searchProps, index]) => this.triggerAmplitudeService.trackAmplitudeEventProps$(
              AMPLITUDE_EVENTS.ADD_TO_CART, { TRAVEL_PRODUCT_TYPE: SearchBy.HOTELS, INDEX: index }, searchProps, productProps
            ))),
          of(null)
        ),
        iif(() => !!isPackageStepDetail, queryParamMap.pipe(take(1)), of(null))
      )),
      tap(([{ hotelData, isPackageStepDetail }, trigger, queries]) => {
        if (isPackageStepDetail) {
          const packageResultsUrl = this.localStorageService.getDecodePackageBaseUrlResult();
          this.facade.setHotelRoomSelected(hotelId);
          this.router.navigateByUrl(hotelResultsToPackageFlightRouteMapper(packageResultsUrl, queries));
        } else {
          if (trigger) {
            triggerAmplitudeEventApp(trigger);
          }
          if (!isNullOrUndefined(roomData?.checkInHour)) {
            this.router.navigate([bookingRootRoute,
              hotelData['SearchId'], hotelData['HotelOptionId'], hotelId, TravelBookingType.HOTELS, 
              JSON.stringify({ checkInHour: roomData?.checkInHour, capacity: roomData?.capacity}) ]);
          } else {
            this.router.navigate([bookingRootRoute,
              hotelData['SearchId'], hotelData['HotelOptionId'], hotelId, TravelBookingType.HOTELS]);
          }
        }
      })
    ).subscribe().unsubscribe();
  }

  private setCalendarByUrl(params: ParamMap): void {
    if (params.get(DEPARTURE_DATE_HOTEL_DETAIL) && params.get(ARRIVAL_DATE_HOTEL_DETAIL)) {
      this.searchFacade.selectCalendarDates([
        new Date(formatDateByRegex(params.get(DEPARTURE_DATE_HOTEL_DETAIL))),
        new Date(formatDateByRegex(params.get(ARRIVAL_DATE_HOTEL_DETAIL))),
      ]);
    } else {
      this.searchFacade.selectCalendarDates([
        new Date(formatDateByRegex(params.get(DEPARTURE_DATE_HOTEL_DETAIL))),

      ]);
    }
  }

  private setDestination(destinationCode: string, placeType: PlaceType, isDestination: boolean = true): void {
    if (destinationCode) {
      this.placePipe.transform(destinationCode).pipe(
        filter(placeSearch => !!placeSearch?.country),
        map(placeSearch => ({
          selectPlace: {
            type: placeType, description: placeSearch.city, code: placeSearch.cityCode, country: placeSearch.country
          } as SelectAutocompletePlace
        })),
        tap(({ selectPlace }) => this.searchFacade.selectPlace(selectPlace, isDestination))
      ).subscribe().unsubscribe();
    }
  }

  private getInputPlaceInfo(codeInfo: string, codeByDetail: string, fullAddress: string, hotelName: string): void {
    const codeDataInfo = getDestinationByV2(codeInfo);
    if (codeDataInfo?.Code || codeByDetail) {
      if (codeDataInfo.Id) {
        let input = {
          Id: Number(codeDataInfo.Id),
          NearestIata: codeDataInfo?.Code ? codeDataInfo?.Code : codeByDetail,
          InputType: codeDataInfo?.Type as HotelDestinationInputType,
          isTravelCitySearch: false,
          Type: null,
          address: null,
          NameFull: null,
          hotel_name: null,
        };
        this.placeNamePipe.transform(input.NearestIata, PlaceSelectors.Name).pipe(
          tap((placeName) => {
            input = {
              ...input,
              address: input?.InputType === HotelDestinationInputType.hotel ? fullAddress : null,
              hotel_name: input?.InputType === HotelDestinationInputType.hotel ? hotelName : null,
              NameFull: input?.InputType === HotelDestinationInputType.location ? placeName : null,
            };
            this.searchFacade.selectPackageDestinationPlace(input);
          })
        ).subscribe().unsubscribe();
      } else {
        this.departurePipe.transform(codeDataInfo.Code).pipe(
          filter(departurePlace => !!departurePlace?.country),
          tap((departurePlace) => {
            this.searchFacade.selectPackageOriginPlace({
              Id: 0,
              Name: departurePlace?.city,
              NameFull: `${departurePlace?.city}, ${departurePlace?.country}`,
              Type: departurePlace?.is_airport ? LocationType.Airport : null,
              NearestIata: departurePlace?.cityCode,
              InputType: HotelDestinationInputType.location,
              isTravelCitySearch: false,
            });
          })
        ).subscribe().unsubscribe();
      }
    }
  }

  private setGuestsByUrlParams(fluxtype: HotelDetailFluxType): void {
    const seachRooms: Room[] = [];
    this.hotelParams.Rooms.forEach((room, index) => {
      if (index > 0) {
        this.searchFacade.addNewRoom({ id: index, [TravelerTypes.ADULTS]: 2, [TravelerTypes.CHILDREN]: 0, childrenAges: [] });
      }
      const guestRoom = {
        id: index, [TravelerTypes.ADULTS]: room.Adults, [TravelerTypes.CHILDREN]: room.Children, childrenAges: room.ChildrenAges
      };
      this.searchFacade.setGuestsPerRoom(guestRoom);
      if (isPackageHotelDetail(fluxtype)) seachRooms.push(guestRoom);
    });
    if (isPackageHotelDetail(fluxtype)) {
      this.guestToPassengerUIPipe.transform(seachRooms)?.map(passenger => this.searchFacade.setPassengerArrangement(passenger));
    }
  }

  private resetGuestData(fluxType: HotelDetailFluxType): void {
    this.searchFacade.resetGuestsPerRoom();
    this.setGuestsByUrlParams(fluxType);
  }
}
